import React, { useState } from "react";
import { Badge } from "react-bootstrap";
import { ChevronDown, ChevronRight, Copy, Check } from "react-feather";
import { isConfirmedNotApplicableMessage } from "../../constants/coupons";

// ─── Small building blocks ──────────────────────────────────────────────────

export const Code: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <code
    className="small text-break"
    style={{
      background: "rgba(0,0,0,0.06)",
      padding: "1px 5px",
      borderRadius: 3,
    }}
  >
    {children}
  </code>
);

const JsonBlock: React.FC<{ label: string; value: any }> = ({
  label,
  value,
}) => {
  const [open, setOpen] = useState(false);
  const isArray = Array.isArray(value);
  const count = isArray ? value.length : Object.keys(value ?? {}).length;
  const noun = isArray ? "item" : "key";

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn btn-sm btn-light border d-inline-flex align-items-center gap-1 py-0 px-2"
        style={{ fontSize: 11 }}
      >
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        {label} ({count} {noun}
        {count === 1 ? "" : "s"})
      </button>
      {open && (
        <pre
          className="bg-light border rounded p-2 mt-1 mb-0"
          style={{ fontSize: 11, maxHeight: 320, overflow: "auto" }}
        >
          {JSON.stringify(value, null, 2)}
        </pre>
      )}
    </div>
  );
};

const Pills: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="d-inline-flex align-items-center flex-wrap gap-2">
    {children}
  </span>
);

/**
 * Long URLs embedded in log text (redirect chains, checkout links with a
 * page of query params) blow up the row width instead of reading as a log
 * line — truncate with a copy button for the full value instead.
 */
const TruncatedUrl: React.FC<{ url: string }> = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const short = url.length > 40 ? `${url.slice(0, 40)}…` : url;
  return (
    <span className="d-inline-flex align-items-center gap-1">
      <Code>{short}</Code>
      <button
        type="button"
        className="btn btn-sm btn-link p-0 text-muted"
        title="Copy full URL"
        onClick={() => {
          navigator.clipboard.writeText(url);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
    </span>
  );
};

// ─── Pattern matchers ───────────────────────────────────────────────────────
// Each entry tries a regex against the raw log text; the first match wins and
// its `render` produces a nicer element. Falls through to plain text (with
// "quoted" substrings lightly highlighted) when nothing matches.

interface Matcher {
  regex: RegExp;
  render: (m: RegExpMatchArray) => React.ReactNode;
}

const matchers: Matcher[] = [
  // QA clicked "pass"/"fail" on auto/SKU report
  {
    regex: /^QA clicked "(pass|fail)" on (auto|SKU) report$/,
    render: (m) => {
      const [, verdict, which] = m;
      const ok = verdict === "pass";
      return (
        <Pills>
          <Badge bg={ok ? "success" : "danger"}>
            {ok ? "👍 Pass" : "👎 Fail"}
          </Badge>
          <span className="text-muted">{which} report</span>
        </Pills>
      );
    },
  },
  // saveAutoTemplate/saveSkuTemplate response: {"outcome":"saved"}
  {
    regex: /^(saveAutoTemplate|saveSkuTemplate) response: (\{[^}]*\})$/,
    render: (m) => {
      const [, fn, json] = m;
      let parsed: { outcome?: string; error?: string };
      try {
        parsed = JSON.parse(json);
      } catch {
        return null;
      }
      const outcome = parsed.outcome ?? "unknown";
      const skipped = outcome.startsWith("skipped");
      const ok = outcome === "saved";
      const bg = ok ? "success" : skipped ? "secondary" : "danger";
      const label = ok
        ? "✓ saved"
        : skipped
          ? `○ ${outcome.replace("-", " — ")}`
          : "✗ failed";
      return (
        <Pills>
          <Badge bg="light" text="dark" className="border">
            {fn}
          </Badge>
          <Badge bg={bg}>{label}</Badge>
          {parsed.error && <span className="text-muted">{parsed.error}</span>}
        </Pills>
      );
    },
  },
  // Verdict persisted ({"autoTemplateStatus":"pass",...}) → response: {...}
  {
    regex: /^Verdict persisted \((\{[^}]*\})\) → response: ([\s\S]*)$/,
    render: (m) => {
      const [, partialJson, responseText] = m;
      let partial: Record<string, unknown>;
      try {
        partial = JSON.parse(partialJson);
      } catch {
        return null;
      }
      const statusKey = Object.keys(partial).find((k) =>
        k.toLowerCase().endsWith("status")
      );
      const status = statusKey ? String(partial[statusKey]) : undefined;
      const reasonKey = Object.keys(partial).find((k) =>
        k.toLowerCase().includes("reason")
      );
      const reason = reasonKey ? partial[reasonKey] : null;
      const ok = status === "pass";

      let responseNode: React.ReactNode = null;
      const trimmed = responseText.trim();
      if (trimmed) {
        try {
          const parsedResponse = JSON.parse(trimmed);
          responseNode =
            parsedResponse && typeof parsedResponse === "object" ? (
              <JsonBlock label="response" value={parsedResponse} />
            ) : (
              <Code>{String(parsedResponse)}</Code>
            );
        } catch {
          responseNode = <span className="text-muted">{trimmed}</span>;
        }
      }

      return (
        <Pills>
          <Badge bg="light" text="dark" className="border">
            Verdict persisted
          </Badge>
          {status && (
            <Badge bg={ok ? "success" : "danger"}>
              {ok ? "👍 pass" : "👎 fail"}
            </Badge>
          )}
          {reason ? <span className="text-muted">{String(reason)}</span> : null}
          {responseNode}
        </Pills>
      );
    },
  },
  // "Run finished: 1/3 code(s) worked"
  {
    regex: /^Run finished: (\d+)\/(\d+) code\(s\) worked$/,
    render: (m) => {
      const [, workedStr, totalStr] = m;
      const worked = Number(workedStr);
      const total = Number(totalStr);
      const emoji = worked === 0 ? "❌" : worked === total ? "🎉" : "⚠️";
      const bg = worked === 0 ? "danger" : worked === total ? "success" : "warning";
      return (
        <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>
          {emoji} Run finished — {worked}/{total} code(s) worked
        </Badge>
      );
    },
  },
  // Code "X" → ✓ success (saved £ 5.25)   |   Code "X" → ✗ failed: reason
  {
    regex:
      /^Code "([^"]+)" → (✓|✗) (success|failed)(?: \(saved ([^)]+)\))?(?::\s(.*))?$/,
    render: (m) => {
      const [, code, , outcome, savings, reason] = m;
      const ok = outcome === "success";
      const notApplicable =
        !ok && isConfirmedNotApplicableMessage(code, reason);
      return (
        <Pills>
          <Code>{code}</Code>
          <Badge bg={ok ? "success" : notApplicable ? "info" : "danger"}>
            {ok
              ? "✓ success"
              : notApplicable
                ? "⊘ not applicable"
                : "✗ failed"}
          </Badge>
          {savings && (
            <Badge bg="light" text="dark" className="border">
              saved {savings}
            </Badge>
          )}
          {reason && <span className="text-muted">{reason}</span>}
        </Pills>
      );
    },
  },
  // [1/3] Trying code "X"
  {
    regex: /^\[(\d+)\/(\d+)\] Trying code "([^"]+)"$/,
    render: (m) => {
      const [, idx, total, code] = m;
      return (
        <Pills>
          <Badge bg="primary">
            {idx}/{total}
          </Badge>
          <span>Trying</span>
          <Code>{code}</Code>
        </Pills>
      );
    },
  },
  // Starting auto-coupons run (mode: new, view: mobile)
  {
    regex: /^Starting auto-coupons run \(mode: (\w+), view: (\w+)\)$/,
    render: (m) => {
      const [, mode, view] = m;
      return (
        <Pills>
          <span>▶ Starting run</span>
          <Badge bg={mode === "new" ? "warning" : "info"} text={mode === "new" ? "dark" : undefined}>
            {mode}
          </Badge>
          <Badge bg="secondary">{view}</Badge>
        </Pills>
      );
    },
  },
  // Run abandoned: <reason>  (either the run itself died mid-flight, or it
  // finished but a reviewer never submitted a Pass/Fail verdict)
  {
    regex: /^Run abandoned: (.+)$/,
    render: (m) => {
      const [, reason] = m;
      return (
        <Pills>
          <Badge bg="danger">⚠ Run abandoned</Badge>
          <span className="text-muted">{reason}</span>
        </Pills>
      );
    },
  },
  // setting/loading selector X = Y
  {
    regex: /^(setting|loading) selector (\w+) = (.+)$/,
    render: (m) => {
      const [, verb, name, value] = m;
      return (
        <Pills>
          <Badge bg="light" text="dark" className="border text-capitalize">
            {verb}
          </Badge>
          <Code>{name}</Code>
          <span className="text-muted">=</span>
          <Code>{value}</Code>
        </Pills>
      );
    },
  },
  // querying X with Y
  {
    regex: /^querying (\w+) with (.+)$/,
    render: (m) => {
      const [, name, value] = m;
      return (
        <Pills>
          <Badge bg="light" text="dark" className="border">
            querying
          </Badge>
          <Code>{name}</Code>
          <Code>{value}</Code>
        </Pills>
      );
    },
  },
  // no selector stored for X
  {
    regex: /^no selector stored for (\w+)$/,
    render: (m) => (
      <Pills>
        <Badge bg="light" text="muted" className="border">
          no selector
        </Badge>
        <Code>{m[1]}</Code>
      </Pills>
    ),
  },
  // element not found for X (Y) after Nms
  {
    regex: /^element not found for (\w+) \((.+)\) after (\d+)ms$/,
    render: (m) => {
      const [, name, selector, ms] = m;
      return (
        <Pills>
          <Badge bg="warning" text="dark">
            ⚠ not found
          </Badge>
          <Code>{name}</Code>
          <Code>{selector}</Code>
          <span className="text-muted">after {ms}ms</span>
        </Pills>
      );
    },
  },
  // checkoutPageUrlRegex: <regex>
  {
    regex: /^checkoutPageUrlRegex: (.+)$/,
    render: (m) => (
      <Pills>
        <Badge bg="light" text="dark" className="border">
          checkoutPageUrlRegex
        </Badge>
        <Code>{m[1]}</Code>
      </Pills>
    ),
  },
  // checkoutPageUrlRegex corrected and verified (new)
  {
    regex: /^checkoutPageUrlRegex corrected and verified(?: \((\w+)\))?$/,
    render: (m) => (
      <Pills>
        <Badge bg="success">✓ verified</Badge>
        <span>checkoutPageUrlRegex corrected</span>
        {m[1] && <Badge bg="secondary">{m[1]}</Badge>}
      </Pills>
    ),
  },
  // Existing checkout URL regex verified
  {
    regex: /^Existing checkout URL regex verified$/,
    render: () => (
      <Pills>
        <Badge bg="success">✓ verified</Badge>
        <span>Existing checkoutPageUrlRegex</span>
      </Pills>
    ),
  },
  // Original cart total: £ 33
  {
    regex: /^(Original cart total): (.+)$/i,
    render: (m) => (
      <Pills>
        <span className="text-muted">{m[1]}:</span>
        <Badge bg="light" text="dark" className="border">
          {m[2]}
        </Badge>
      </Pills>
    ),
  },
  // new total price is £ 33
  {
    regex: /^new total price is (.+)$/i,
    render: (m) => (
      <Pills>
        <span className="text-muted">new total price is</span>
        <Badge bg="light" text="dark" className="border">
          {m[1]}
        </Badge>
      </Pills>
    ),
  },
  // Detected checkout platform: shopify
  {
    regex: /^Detected checkout platform: (\w+)$/,
    render: (m) => (
      <Pills>
        <span className="text-muted">Detected checkout platform</span>
        <Badge bg="info">{m[1]}</Badge>
      </Pills>
    ),
  },
  // Clicking submit (selector: X)
  {
    regex: /^Clicking submit \(selector: (.+)\)$/,
    render: (m) => (
      <Pills>
        <span>🖱 Clicking submit</span>
        <Code>{m[1]}</Code>
      </Pills>
    ),
  },
  // extracted N sku item(s), template validity: true|false
  {
    regex: /^extracted (\d+) sku item\(s\), template validity: (true|false)$/,
    render: (m) => {
      const [, count, valid] = m;
      const ok = valid === "true";
      return (
        <Pills>
          <Badge bg="secondary">{count} item(s)</Badge>
          <Badge bg={ok ? "success" : "danger"}>
            {ok ? "✓ valid" : "✗ invalid"}
          </Badge>
        </Pills>
      );
    },
  },
  // "Full evaluations: [{...}]" / any other "<label>: <json>" — collapse the
  // blob. Kept as a late fallback so more specific matchers above (which also
  // parse embedded JSON, e.g. Verdict persisted) get first crack at the line.
  {
    regex: /^(.+?):\s*([[{][\s\S]*[\]}])$/,
    render: (m) => {
      try {
        const parsed = JSON.parse(m[2]);
        return (
          <span>
            {m[1]}: <JsonBlock label={m[1]} value={parsed} />
          </span>
        );
      } catch {
        return null;
      }
    },
  },
  // Any log line with a long URL embedded in it (redirect chains, checkout
  // links with a page of query params) — truncate it, keep the rest as-is.
  {
    regex: /^(.*?)(https?:\/\/\S{40,})(.*)$/,
    render: (m) => {
      const [, before, url, after] = m;
      return (
        <span>
          {before}
          <TruncatedUrl url={url} />
          {after}
        </span>
      );
    },
  },
];

/**
 * Renders a single log entry's text, upgrading recognized shapes (JSON
 * blobs, coupon-code trial results, selector actions, run outcomes) into
 * pills/badges/collapsible blocks. Falls back to the raw text untouched.
 */
export const LogLineText: React.FC<{ text: string }> = ({ text }) => {
  for (const { regex, render } of matchers) {
    const match = text.match(regex);
    if (!match) continue;
    const rendered = render(match);
    if (rendered !== null) return <>{rendered}</>;
  }
  return <>{text}</>;
};
